/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * @fileoverview Defines methods used to actually send HTTP requests from
 * abstract representations.
 */
import firebase from '@firebase/app';
import * as array from './array';
import * as backoff from './backoff';
import * as errorsExports from './error';
import * as object from './object';
import * as promiseimpl from './promise_external';
import * as type from './type';
import * as UrlUtils from './url';
import * as XhrIoExports from './xhrio';
/**
 * @struct
 * @template T
 */
var NetworkRequest = /** @class */ (function () {
 function NetworkRequest(url, method, headers, body, successCodes, additionalRetryCodes, callback, errorCallback, timeout, progressCallback, pool) {
 this.pendingXhr_ = null;
 this.backoffId_ = null;
 this.resolve_ = null;
 this.reject_ = null;
 this.canceled_ = false;
 this.appDelete_ = false;
 this.url_ = url;
 this.method_ = method;
 this.headers_ = headers;
 this.body_ = body;
 this.successCodes_ = successCodes.slice();
 this.additionalRetryCodes_ = additionalRetryCodes.slice();
 this.callback_ = callback;
 this.errorCallback_ = errorCallback;
 this.progressCallback_ = progressCallback;
 this.timeout_ = timeout;
 this.pool_ = pool;
 var self = this;
 this.promise_ = promiseimpl.make(function (resolve, reject) {
 self.resolve_ = resolve;
 self.reject_ = reject;
 self.start_();
 });
 }
 /**
 * Actually starts the retry loop.
 */
 NetworkRequest.prototype.start_ = function () {
 var self = this;
 function doTheRequest(backoffCallback, canceled) {
 if (canceled) {
 backoffCallback(false, new RequestEndStatus(false, null, true));
 return;
 }
 var xhr = self.pool_.createXhrIo();
 self.pendingXhr_ = xhr;
 function progressListener(progressEvent) {
 var loaded = progressEvent.loaded;
 var total = progressEvent.lengthComputable ? progressEvent.total : -1;
 if (self.progressCallback_ !== null) {
 self.progressCallback_(loaded, total);
 }
 }
 if (self.progressCallback_ !== null) {
 xhr.addUploadProgressListener(progressListener);
 }
 xhr
 .send(self.url_, self.method_, self.body_, self.headers_)
 .then(function (xhr) {
 if (self.progressCallback_ !== null) {
 xhr.removeUploadProgressListener(progressListener);
 }
 self.pendingXhr_ = null;
 xhr = xhr;
 var hitServer = xhr.getErrorCode() === XhrIoExports.ErrorCode.NO_ERROR;
 var status = xhr.getStatus();
 if (!hitServer || self.isRetryStatusCode_(status)) {
 var wasCanceled = xhr.getErrorCode() === XhrIoExports.ErrorCode.ABORT;
 backoffCallback(false, new RequestEndStatus(false, null, wasCanceled));
 return;
 }
 var successCode = array.contains(self.successCodes_, status);
 backoffCallback(true, new RequestEndStatus(successCode, xhr));
 });
 }
 /**
 * @param requestWentThrough True if the request eventually went
 * through, false if it hit the retry limit or was canceled.
 */
 function backoffDone(requestWentThrough, status) {
 var resolve = self.resolve_;
 var reject = self.reject_;
 var xhr = status.xhr;
 if (status.wasSuccessCode) {
 try {
 var result = self.callback_(xhr, xhr.getResponseText());
 if (type.isJustDef(result)) {
 resolve(result);
 }
 else {
 resolve();
 }
 }
 catch (e) {
 reject(e);
 }
 }
 else {
 if (xhr !== null) {
 var err = errorsExports.unknown();
 err.setServerResponseProp(xhr.getResponseText());
 if (self.errorCallback_) {
 reject(self.errorCallback_(xhr, err));
 }
 else {
 reject(err);
 }
 }
 else {
 if (status.canceled) {
 var err = self.appDelete_
 ? errorsExports.appDeleted()
 : errorsExports.canceled();
 reject(err);
 }
 else {
 var err = errorsExports.retryLimitExceeded();
 reject(err);
 }
 }
 }
 }
 if (this.canceled_) {
 backoffDone(false, new RequestEndStatus(false, null, true));
 }
 else {
 this.backoffId_ = backoff.start(doTheRequest, backoffDone, this.timeout_);
 }
 };
 /** @inheritDoc */
 NetworkRequest.prototype.getPromise = function () {
 return this.promise_;
 };
 /** @inheritDoc */
 NetworkRequest.prototype.cancel = function (appDelete) {
 this.canceled_ = true;
 this.appDelete_ = appDelete || false;
 if (this.backoffId_ !== null) {
 backoff.stop(this.backoffId_);
 }
 if (this.pendingXhr_ !== null) {
 this.pendingXhr_.abort();
 }
 };
 NetworkRequest.prototype.isRetryStatusCode_ = function (status) {
 // The codes for which to retry came from this page:
 // https://cloud.google.com/storage/docs/exponential-backoff
 var isFiveHundredCode = status >= 500 && status < 600;
 var extraRetryCodes = [
 // Request Timeout: web server didn't receive full request in time.
 408,
 // Too Many Requests: you're getting rate-limited, basically.
 429
 ];
 var isExtraRetryCode = array.contains(extraRetryCodes, status);
 var isRequestSpecificRetryCode = array.contains(this.additionalRetryCodes_, status);
 return isFiveHundredCode || isExtraRetryCode || isRequestSpecificRetryCode;
 };
 return NetworkRequest;
}());
/**
 * A collection of information about the result of a network request.
 * @param opt_canceled Defaults to false.
 * @struct
 */
var RequestEndStatus = /** @class */ (function () {
 function RequestEndStatus(wasSuccessCode, xhr, opt_canceled) {
 this.wasSuccessCode = wasSuccessCode;
 this.xhr = xhr;
 this.canceled = !!opt_canceled;
 }
 return RequestEndStatus;
}());
export { RequestEndStatus };
export function addAuthHeader_(headers, authToken) {
 if (authToken !== null && authToken.length > 0) {
 headers['Authorization'] = 'Firebase ' + authToken;
 }
}
export function addVersionHeader_(headers) {
 var number = typeof firebase !== 'undefined' ? firebase.SDK_VERSION : 'AppManager';
 headers['X-Firebase-Storage-Version'] = 'webjs/' + number;
}
/**
 * @template T
 */
export function makeRequest(requestInfo, authToken, pool) {
 var queryPart = UrlUtils.makeQueryString(requestInfo.urlParams);
 var url = requestInfo.url + queryPart;
 var headers = object.clone(requestInfo.headers);
 addAuthHeader_(headers, authToken);
 addVersionHeader_(headers);
 return new NetworkRequest(url, requestInfo.method, headers, requestInfo.body, requestInfo.successCodes, requestInfo.additionalRetryCodes, requestInfo.handler, requestInfo.errorHandler, requestInfo.timeout, requestInfo.progressCallback, pool);
}

//# sourceMappingURL=request.js.map



//////////////////
// WEBPACK FOOTER
// ../storage/dist/esm/src/implementation/request.js
// module id = null
// module chunks = 